import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { products as productsApi, stockLots, addresses, getImageUrl } from '../lib/api';
import { useCart } from '../context/CartContext';
import { useAuth } from '../context/AuthContext';
import { useActivity } from '../context/ActivityContext';
import BulkOrderMatrix from '../components/BulkOrderMatrix';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '../components/ui/dialog';
import { toast } from 'sonner';
import { ArrowLeft, ChevronLeft, ChevronRight, Package, Tag, AlertTriangle, Loader2, CheckCircle, Clock } from 'lucide-react';
import usePageTitle from '../hooks/usePageTitle';

const ProductDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { addToCart, loading: cartLoading } = useCart();
  const { isAuthenticated, user } = useAuth();
  const { trackProductView } = useActivity();
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedImage, setSelectedImage] = useState(0);
  const [mainImageLoaded, setMainImageLoaded] = useState(false);
  
  // Stock Lot states
  const [stockLotDetails, setStockLotDetails] = useState(null);
  const [showStockLotCheckout, setShowStockLotCheckout] = useState(false);
  const [purchaseLoading, setPurchaseLoading] = useState(false);
  const [purchaseComplete, setPurchaseComplete] = useState(false);
  const [orderNumber, setOrderNumber] = useState('');
  
  // Address states
  const [savedAddresses, setSavedAddresses] = useState([]);
  const [selectedAddressId, setSelectedAddressId] = useState('new');
  const [shippingAddress, setShippingAddress] = useState({
    street: '',
    city: '',
    state: '',
    postal_code: '',
    country: 'Australia',
    contact_person: '',
    contact_phone: ''
  });
  const [poNumber, setPoNumber] = useState('');
  const [deliveryNotes, setDeliveryNotes] = useState('');

  // Set page title when product loads
  usePageTitle(product ? product.name : 'Loading...');

  useEffect(() => {
    loadProduct();
  }, [id]);

  useEffect(() => {
    if (isAuthenticated) {
      loadSavedAddresses();
    }
  }, [isAuthenticated]);

  const loadProduct = async () => {
    setLoading(true);
    try {
      const response = await productsApi.getById(id);
      setProduct(response.data);
      
      // Track product view
      trackProductView(response.data);
      
      // If it's a stock lot, load details
      if (response.data.is_stock_lot) {
        try {
          const lotResponse = await stockLots.getDetails(id);
          setStockLotDetails(lotResponse.data);
        } catch (err) {
          console.error('Failed to load stock lot details');
        }
      }
    } catch (error) {
      toast.error('Product not found');
      navigate('/products');
    } finally {
      setLoading(false);
    }
  };

  const loadSavedAddresses = async () => {
    try {
      const response = await addresses.getAll();
      setSavedAddresses(response.data);
    } catch (error) {
      console.error('Failed to load addresses');
    }
  };

  const handleAddressSelect = (addressId) => {
    setSelectedAddressId(addressId);
    if (addressId !== 'new') {
      const addr = savedAddresses.find(a => a.id === addressId);
      if (addr) {
        setShippingAddress({
          street: addr.street || '',
          city: addr.city || '',
          state: addr.state || '',
          postal_code: addr.postal_code || '',
          country: addr.country || 'Australia',
          contact_person: addr.contact_person || '',
          contact_phone: addr.contact_phone || ''
        });
      }
    } else {
      setShippingAddress({
        street: '',
        city: '',
        state: '',
        postal_code: '',
        country: 'Australia',
        contact_person: '',
        contact_phone: ''
      });
    }
  };

  const handleAddToCart = async (sizes) => {
    if (!isAuthenticated) {
      toast.error('Please login to add items to cart');
      navigate('/login');
      return;
    }

    try {
      await addToCart(product.id, sizes);
      toast.success('Added to cart successfully!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add to cart');
    }
  };

  const handleStockLotPurchase = async () => {
    if (!shippingAddress.street || !shippingAddress.city || !shippingAddress.state || !shippingAddress.postal_code) {
      toast.error('Please fill in all required address fields');
      return;
    }

    setPurchaseLoading(true);
    try {
      const response = await stockLots.purchase({
        product_id: product.id,
        shipping_address: shippingAddress,
        delivery_notes: deliveryNotes || null,
        po_number: poNumber || null,
        save_address: selectedAddressId === 'new'
      });
      
      setOrderNumber(response.data.order.order_number);
      setPurchaseComplete(true);
      toast.success('Stock lot purchased successfully!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to purchase stock lot');
    } finally {
      setPurchaseLoading(false);
    }
  };

  const nextImage = () => {
    if (product?.images?.length > 1) {
      setMainImageLoaded(false);
      setSelectedImage((prev) => (prev + 1) % product.images.length);
    }
  };

  const prevImage = () => {
    if (product?.images?.length > 1) {
      setMainImageLoaded(false);
      setSelectedImage((prev) => (prev - 1 + product.images.length) % product.images.length);
    }
  };

  if (loading) {
    return (
      <div className="container-wide py-8">
        <div className="animate-pulse grid grid-cols-1 lg:grid-cols-2 gap-12">
          <div className="aspect-square bg-[#F1F5F9]" />
          <div className="space-y-4">
            <div className="h-8 bg-[#F1F5F9] w-3/4" />
            <div className="h-4 bg-[#F1F5F9] w-1/2" />
            <div className="h-32 bg-[#F1F5F9]" />
          </div>
        </div>
      </div>
    );
  }

  if (!product) return null;

  const totalStock = product.inventory?.reduce((acc, inv) => acc + inv.quantity, 0) || 0;
  const isStockLot = product.is_stock_lot && product.is_discontinued;
  const isOnSale = product.is_on_sale && !isStockLot;
  const isSoldOut = product.is_sold_out || (isStockLot && totalStock === 0);

  // Sale type labels
  const saleTypeLabels = {
    'black_friday': 'BLACK FRIDAY',
    'boxing_day': 'BOXING DAY',
    'australia_day': 'AUSTRALIA DAY',
    'christmas': 'CHRISTMAS SALE',
    'new_year': 'NEW YEAR SALE',
    'eofy': 'EOFY SALE',
    'end_of_season': 'END OF SEASON',
    'clearance': 'CLEARANCE',
    'flash_sale': 'FLASH SALE',
  };

  // Get sale badge text
  const getSaleBadgeText = () => {
    if (product.sale_type === 'custom' && product.sale_type_custom) {
      return product.sale_type_custom.toUpperCase();
    }
    return saleTypeLabels[product.sale_type] || 'SALE';
  };

  // Stock Lot Pricing Calculations
  const originalTotal = isStockLot ? totalStock * product.price : 0;
  const stockLotPrice = product.stock_lot_price || 0;
  const discountAmount = originalTotal - stockLotPrice;
  const discountPercentage = originalTotal > 0 ? Math.round((discountAmount / originalTotal) * 100) : 0;
  
  // Sale Pricing
  const saleDiscount = isOnSale && product.price > 0 
    ? Math.round((1 - (product.sale_price || 0) / product.price) * 100) 
    : 0;

  return (
    <div className="container-wide py-8 md:py-12 fade-in">
      {/* Back Button */}
      <Link
        to="/products"
        className="inline-flex items-center text-sm text-[#64748B] hover:text-[#0F172A] mb-8"
        data-testid="back-to-products"
      >
        <ArrowLeft className="w-4 h-4 mr-2" />
        Back to Products
      </Link>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-16">
        {/* Image Gallery - Left Side (Sticky) */}
        <div className="lg:sticky lg:top-24 lg:self-start">
          {/* Sold Out Badge */}
          {isSoldOut && (
            <div className="absolute top-4 left-4 z-10">
              <Badge className="bg-gray-900 text-white px-4 py-2 text-lg font-bold">
                SOLD OUT
              </Badge>
            </div>
          )}
          {/* Stock Lot Badge */}
          {isStockLot && !isSoldOut && (
            <div className="absolute top-4 left-4 z-10">
              <Badge className="bg-orange-500 text-white px-3 py-1 text-sm font-semibold">
                <Tag className="w-4 h-4 mr-1" />
                STOCK LOT SALE
              </Badge>
            </div>
          )}
          {/* Sale Badge */}
          {isOnSale && !isSoldOut && (
            <div className="absolute top-4 left-4 z-10 flex flex-col gap-1">
              <Badge className="bg-red-500 text-white px-3 py-1 text-sm font-bold">
                {getSaleBadgeText()}
              </Badge>
              {saleDiscount > 0 && (
                <Badge className="bg-red-600 text-white px-2 py-0.5 text-xs">
                  {saleDiscount}% OFF
                </Badge>
              )}
            </div>
          )}
          
          <div className="relative aspect-square bg-gray-100 overflow-hidden mb-4 group cursor-zoom-in">
            {/* Loading skeleton */}
            {!mainImageLoaded && (
              <div className="absolute inset-0 bg-gray-200 animate-pulse" />
            )}
            <img
              src={getImageUrl(product.images?.[selectedImage]) || 'https://images.unsplash.com/photo-1523381210434-271e8be1f52b?w=600'}
              alt={product.name}
              className={`w-full h-full object-cover transition-all duration-500 ease-out group-hover:scale-[1.6] ${
                mainImageLoaded ? 'opacity-100' : 'opacity-0'
              }`}
              data-testid="product-main-image"
              onLoad={() => setMainImageLoaded(true)}
              onError={(e) => {
                e.target.src = 'https://images.unsplash.com/photo-1523381210434-271e8be1f52b?w=600';
                setMainImageLoaded(true);
              }}
            />
            
            {product.images?.length > 1 && (
              <>
                <button
                  onClick={prevImage}
                  className="absolute left-4 top-1/2 -translate-y-1/2 w-10 h-10 bg-white shadow-md flex items-center justify-center hover:bg-[#F8FAFC] z-10"
                  data-testid="prev-image-btn"
                >
                  <ChevronLeft className="w-5 h-5" />
                </button>
                <button
                  onClick={nextImage}
                  className="absolute right-4 top-1/2 -translate-y-1/2 w-10 h-10 bg-white shadow-md flex items-center justify-center hover:bg-[#F8FAFC] z-10"
                  data-testid="next-image-btn"
                >
                  <ChevronRight className="w-5 h-5" />
                </button>
              </>
            )}
          </div>

          {/* Thumbnails */}
          {product.images?.length > 1 && (
            <div className="flex gap-2">
              {product.images.map((img, idx) => (
                <button
                  key={idx}
                  onClick={() => {
                    if (idx !== selectedImage) {
                      setMainImageLoaded(false);
                      setSelectedImage(idx);
                    }
                  }}
                  className={`w-20 h-20 border-2 overflow-hidden group ${
                    selectedImage === idx ? 'border-[#002FA7]' : 'border-transparent'
                  }`}
                >
                  <img 
                    src={getImageUrl(img)} 
                    alt="" 
                    className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-110"
                    loading="lazy"
                  />
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Product Details - Right Side */}
        <div className="space-y-6">
          {/* Coming Soon Banner */}
          {product.is_coming_soon && (
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center">
                  <Clock className="w-6 h-6 text-blue-600" />
                </div>
                <div>
                  <h3 className="font-bold text-blue-900 text-lg">New Product Coming Soon</h3>
                  <p className="text-blue-700 text-sm">
                    This product is not yet available.
                    {product.expected_date && (
                      <span className="font-semibold"> Due in store: {new Date(product.expected_date).toLocaleDateString('en-AU', { month: 'long', day: 'numeric', year: 'numeric' })}</span>
                    )}
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Header */}
          <div>
            <div className="flex items-center gap-2 mb-2 flex-wrap">
              <Badge variant="outline" className="text-xs text-[#64748B] border-[#E2E8F0]">
                {product.category}
              </Badge>
              <span className="text-xs font-mono text-[#64748B]">{product.sku}</span>
              {product.is_discontinued && (
                <Badge className="bg-red-100 text-red-700 text-xs">
                  Discontinued
                </Badge>
              )}
              {product.is_coming_soon && (
                <Badge className="bg-blue-500 text-white text-xs">
                  <Clock className="w-3 h-3 mr-1" />
                  New Arrival Coming Soon
                </Badge>
              )}
              {product.is_new && !product.is_coming_soon && (
                <Badge className="bg-green-500 text-white text-xs">
                  NEW
                </Badge>
              )}
            </div>
            <h1 className="text-2xl md:text-3xl font-bold text-[#0F172A] mb-4" data-testid="product-name">
              {product.name}
            </h1>
            
            {/* Pricing - Different display for stock lot, sale, or regular */}
            {product.is_coming_soon ? (
              <div className="text-[#64748B]">
                <p className="text-lg font-semibold">Price available soon</p>
                {product.price > 0 && isAuthenticated && (
                  <p className="text-sm">Estimated: ${product.price.toFixed(2)} per unit</p>
                )}
              </div>
            ) : isStockLot ? (
              isAuthenticated ? (
                <div className="space-y-2">
                  <div className="flex items-center gap-3">
                    <span className="text-2xl text-[#64748B] line-through">
                      ${originalTotal.toFixed(2)}
                    </span>
                    <Badge className="bg-green-500 text-white">
                      Save {discountPercentage}%
                    </Badge>
                  </div>
                  <div className="flex items-baseline gap-2">
                    <span className="text-4xl font-bold text-green-600" data-testid="stock-lot-price">
                      ${stockLotPrice.toFixed(2)}
                    </span>
                    <span className="text-sm text-[#64748B]">for entire stock lot</span>
                  </div>
                  <p className="text-sm text-[#64748B]">
                    Original price: ${product.price?.toFixed(2)} per unit × {totalStock} units
                  </p>
                </div>
              ) : (
                <div className="text-[#64748B]">
                  <p className="text-lg font-semibold">Login to see stock lot pricing</p>
                  <p className="text-sm">Total units available: {totalStock}</p>
                </div>
              )
            ) : isOnSale ? (
              isAuthenticated ? (
                <div className="space-y-2">
                  <div className="flex items-center gap-3">
                    <span className="text-2xl text-[#64748B] line-through">
                      ${product.price?.toFixed(2)}
                    </span>
                    <Badge className="bg-red-500 text-white">
                      Save {saleDiscount}%
                    </Badge>
                  </div>
                  <div className="flex items-baseline gap-2">
                    <span className="text-4xl font-bold text-red-600" data-testid="sale-price">
                      ${product.sale_price?.toFixed(2)}
                    </span>
                    <span className="text-sm text-[#64748B]">per unit (Sale)</span>
                  </div>
                </div>
              ) : (
                <div className="text-[#64748B]">
                  <p className="text-lg font-semibold">Login to see sale pricing</p>
                </div>
              )
            ) : (
              product.price !== undefined ? (
                <div className="flex items-baseline gap-4">
                  <span className="text-3xl font-bold text-[#002FA7]" data-testid="product-price">
                    ${product.price?.toFixed(2)}
                  </span>
                  <span className="text-sm text-[#64748B]">per unit</span>
                </div>
              ) : (
                <div className="text-[#64748B]">
                  <p className="text-lg">Login to see pricing</p>
                </div>
              )
            )}
          </div>

          {/* Sale Alert Banner */}
          {isOnSale && (
            <div className="bg-red-50 border border-red-200 p-4 rounded-lg">
              <div className="flex items-start gap-3">
                <Tag className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
                <div>
                  <h4 className="font-semibold text-red-800 mb-1">
                    On Sale – Limited Time Offer
                  </h4>
                  <p className="text-sm text-red-700">
                    This product is currently on sale at a discounted price. Order now to save!
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Stock Lot Alert Banner */}
          {isStockLot && (
            <div className="bg-orange-50 border border-orange-200 p-4 rounded-lg">
              <div className="flex items-start gap-3">
                <AlertTriangle className="w-5 h-5 text-orange-500 flex-shrink-0 mt-0.5" />
                <div>
                  <h4 className="font-semibold text-orange-800 mb-1">
                    Discontinued – Stock Lot Sale
                  </h4>
                  <p className="text-sm text-orange-700">
                    This product is discontinued. All remaining stock must be purchased as a complete lot.
                    No partial orders or size selection available.
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Description */}
          <div>
            <h3 className="text-sm font-semibold text-[#0F172A] mb-2">Description</h3>
            <p className="text-[#64748B] leading-relaxed" data-testid="product-description">
              {product.description}
            </p>
          </div>

          {/* Stock Summary - Different for Stock Lot */}
          {isStockLot ? (
            <div className="border border-[#E2E8F0] rounded-lg overflow-hidden">
              <div className="bg-[#F8FAFC] px-4 py-3 border-b border-[#E2E8F0]">
                <h3 className="font-semibold text-[#0F172A] flex items-center gap-2">
                  <Package className="w-4 h-4" />
                  Stock Lot Contents
                </h3>
              </div>
              <div className="p-4">
                <div className="grid grid-cols-2 gap-3 mb-4">
                  {product.inventory?.filter(inv => inv.quantity > 0).map(inv => (
                    <div key={inv.size} className="flex justify-between items-center p-2 bg-gray-50 rounded">
                      <span className="font-medium text-[#0F172A]">
                        Size {inv.size === 'OS' ? 'One Size' : inv.size}
                      </span>
                      <Badge variant="secondary">{inv.quantity} units</Badge>
                    </div>
                  ))}
                </div>
                <div className="flex justify-between items-center pt-3 border-t border-[#E2E8F0]">
                  <span className="font-semibold text-[#0F172A]">Total Units</span>
                  <span className="text-xl font-bold text-[#002FA7]">{totalStock}</span>
                </div>
              </div>
            </div>
          ) : (
            <div className="p-4 bg-[#F8FAFC] border border-[#E2E8F0]">
              <div className="flex justify-between items-center">
                <span className="text-sm text-[#64748B]">Total Available Stock</span>
                <span className="text-lg font-semibold text-[#0F172A]" data-testid="total-stock">
                  {totalStock} units
                </span>
              </div>
              <div className="flex justify-between items-center mt-2">
                <span className="text-sm text-[#64748B]">Sizing Type</span>
                <Badge variant="secondary" className="text-xs">
                  {product.sizing_type === 'onesize' ? 'One Size' : product.sizing_type === 'ladyfit' ? 'Lady-fit' : product.sizing_type === 'kids' ? 'Kids' : product.sizing_type === 'combined' ? 'Combined (S/M, L/XL)' : 'Unisex'}
                </Badge>
              </div>
            </div>
          )}

          {/* Buy Stock Lot Button or Regular Bulk Order Matrix */}
          {isStockLot ? (
            <div className="space-y-4">
              {totalStock > 0 ? (
                <>
                  <Button
                    onClick={() => {
                      if (!isAuthenticated) {
                        toast.error('Please login to purchase');
                        navigate('/login');
                        return;
                      }
                      setShowStockLotCheckout(true);
                    }}
                    className="w-full h-14 bg-green-600 hover:bg-green-700 text-white text-lg font-semibold"
                    data-testid="buy-stock-lot-btn"
                  >
                    <Package className="w-5 h-5 mr-2" />
                    Buy Entire Stock Lot - ${stockLotPrice.toFixed(2)}
                  </Button>
                  <p className="text-center text-sm text-[#64748B]">
                    You save ${discountAmount.toFixed(2)} ({discountPercentage}% off)
                  </p>
                </>
              ) : (
                <div className="p-4 bg-gray-100 rounded-lg text-center">
                  <p className="font-semibold text-gray-600">Stock Lot Sold Out</p>
                  <p className="text-sm text-gray-500 mt-1">This stock lot has been purchased</p>
                </div>
              )}
            </div>
          ) : product.is_coming_soon ? (
            /* Coming Soon - Allow ordering with notice */
            <div className="space-y-4">
              <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
                <div className="flex items-center gap-2 text-blue-800">
                  <Clock className="w-5 h-5" />
                  <span className="font-semibold">New Arrival Coming Soon</span>
                </div>
                <p className="text-blue-700 text-sm mt-1">
                  Order now and we'll process it when stock arrives.
                  {product.expected_date && (
                    <span className="font-medium"> Due in store: {new Date(product.expected_date).toLocaleDateString('en-AU', { month: 'long', day: 'numeric', year: 'numeric' })}</span>
                  )}
                </p>
              </div>
              <BulkOrderMatrix
                product={product}
                onAddToCart={handleAddToCart}
                loading={cartLoading}
              />
            </div>
          ) : (
            <BulkOrderMatrix
              product={product}
              onAddToCart={handleAddToCart}
              loading={cartLoading}
            />
          )}

          {/* Not logged in message */}
          {!isAuthenticated && !product.is_coming_soon && (
            <div className="p-4 bg-[#FEF3C7] border border-[#F59E0B] text-sm text-[#92400E]">
              Please <Link to="/login" className="font-semibold underline">login</Link> or{' '}
              <Link to="/register" className="font-semibold underline">register</Link> to place orders.
            </div>
          )}
        </div>
      </div>

      {/* Stock Lot Checkout Dialog */}
      <Dialog open={showStockLotCheckout} onOpenChange={setShowStockLotCheckout}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          {purchaseComplete ? (
            <div className="text-center py-8">
              <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
              <h2 className="text-2xl font-bold text-[#0F172A] mb-2">Order Placed Successfully!</h2>
              <p className="text-[#64748B] mb-2">Thank you for your purchase.</p>
              <p className="text-lg font-mono text-[#002FA7] mb-6">Order Number: {orderNumber}</p>
              <div className="flex gap-4 justify-center">
                <Button onClick={() => navigate('/orders')} className="bg-[#002FA7]">
                  View Orders
                </Button>
                <Button variant="outline" onClick={() => navigate('/products')}>
                  Continue Shopping
                </Button>
              </div>
            </div>
          ) : (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  <Package className="w-5 h-5 text-green-600" />
                  Complete Stock Lot Purchase
                </DialogTitle>
                <DialogDescription>
                  You are purchasing the entire stock lot of {product.name}
                </DialogDescription>
              </DialogHeader>

              <div className="space-y-6 mt-4">
                {/* Order Summary */}
                <div className="bg-gray-50 p-4 rounded-lg">
                  <h4 className="font-semibold mb-3">Order Summary</h4>
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span>Product</span>
                      <span className="font-medium">{product.name}</span>
                    </div>
                    <div className="flex justify-between">
                      <span>Total Units</span>
                      <span>{totalStock} units</span>
                    </div>
                    <div className="flex justify-between text-[#64748B]">
                      <span>Original Total</span>
                      <span className="line-through">${originalTotal.toFixed(2)}</span>
                    </div>
                    <div className="flex justify-between text-green-600 font-semibold text-base pt-2 border-t">
                      <span>Stock Lot Price</span>
                      <span>${stockLotPrice.toFixed(2)}</span>
                    </div>
                    <div className="flex justify-between text-[#64748B]">
                      <span>GST (10%)</span>
                      <span>${(stockLotPrice * 0.1).toFixed(2)}</span>
                    </div>
                    <div className="flex justify-between font-bold text-lg pt-2 border-t">
                      <span>Total (inc GST)</span>
                      <span>${(stockLotPrice * 1.1).toFixed(2)}</span>
                    </div>
                  </div>
                </div>

                {/* PO Number */}
                <div>
                  <Label htmlFor="po_number">Purchase Order Number (Optional)</Label>
                  <Input
                    id="po_number"
                    value={poNumber}
                    onChange={(e) => setPoNumber(e.target.value)}
                    placeholder="Your PO reference"
                    className="mt-1"
                  />
                </div>

                {/* Shipping Address */}
                <div className="space-y-4">
                  <h4 className="font-semibold">Shipping Address</h4>
                  
                  {savedAddresses.length > 0 && (
                    <div>
                      <Label>Select Address</Label>
                      <Select value={selectedAddressId} onValueChange={handleAddressSelect}>
                        <SelectTrigger className="mt-1">
                          <SelectValue placeholder="Choose an address" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="new">Enter New Address</SelectItem>
                          {savedAddresses.map(addr => (
                            <SelectItem key={addr.id} value={addr.id}>
                              {addr.label || addr.street}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  )}

                  <div className="grid grid-cols-2 gap-4">
                    <div className="col-span-2">
                      <Label htmlFor="street">Street Address *</Label>
                      <Input
                        id="street"
                        value={shippingAddress.street}
                        onChange={(e) => setShippingAddress(prev => ({ ...prev, street: e.target.value }))}
                        placeholder="123 Business Street"
                        className="mt-1"
                        required
                      />
                    </div>
                    <div>
                      <Label htmlFor="city">City *</Label>
                      <Input
                        id="city"
                        value={shippingAddress.city}
                        onChange={(e) => setShippingAddress(prev => ({ ...prev, city: e.target.value }))}
                        placeholder="Sydney"
                        className="mt-1"
                        required
                      />
                    </div>
                    <div>
                      <Label htmlFor="state">State *</Label>
                      <Input
                        id="state"
                        value={shippingAddress.state}
                        onChange={(e) => setShippingAddress(prev => ({ ...prev, state: e.target.value }))}
                        placeholder="NSW"
                        className="mt-1"
                        required
                      />
                    </div>
                    <div>
                      <Label htmlFor="postal_code">Postal Code *</Label>
                      <Input
                        id="postal_code"
                        value={shippingAddress.postal_code}
                        onChange={(e) => setShippingAddress(prev => ({ ...prev, postal_code: e.target.value }))}
                        placeholder="2000"
                        className="mt-1"
                        required
                      />
                    </div>
                    <div>
                      <Label htmlFor="country">Country</Label>
                      <Input
                        id="country"
                        value={shippingAddress.country}
                        onChange={(e) => setShippingAddress(prev => ({ ...prev, country: e.target.value }))}
                        className="mt-1"
                      />
                    </div>
                    <div>
                      <Label htmlFor="contact_person">Contact Person</Label>
                      <Input
                        id="contact_person"
                        value={shippingAddress.contact_person}
                        onChange={(e) => setShippingAddress(prev => ({ ...prev, contact_person: e.target.value }))}
                        placeholder="John Smith"
                        className="mt-1"
                      />
                    </div>
                    <div>
                      <Label htmlFor="contact_phone">Contact Phone</Label>
                      <Input
                        id="contact_phone"
                        value={shippingAddress.contact_phone}
                        onChange={(e) => setShippingAddress(prev => ({ ...prev, contact_phone: e.target.value }))}
                        placeholder="+61 400 000 000"
                        className="mt-1"
                      />
                    </div>
                  </div>
                </div>

                {/* Delivery Notes */}
                <div>
                  <Label htmlFor="delivery_notes">Delivery Notes (Optional)</Label>
                  <Textarea
                    id="delivery_notes"
                    value={deliveryNotes}
                    onChange={(e) => setDeliveryNotes(e.target.value)}
                    placeholder="Special delivery instructions..."
                    className="mt-1"
                    rows={3}
                  />
                </div>

                {/* Purchase Button */}
                <Button
                  onClick={handleStockLotPurchase}
                  disabled={purchaseLoading}
                  className="w-full h-12 bg-green-600 hover:bg-green-700 text-white font-semibold"
                >
                  {purchaseLoading ? (
                    <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Processing...</>
                  ) : (
                    `Complete Purchase - $${(stockLotPrice * 1.1).toFixed(2)}`
                  )}
                </Button>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ProductDetailPage;
